<template>
  <HrPageLayout
    title="Security Settings"
    subtitle="Manage and update your account security credentials"
  >
    <div class="change-password-page">

            <!-- Profile Summary & Password Expiry Banner -->
            <div class="profile-security-banner mb-4">
              <div class="banner-overlay"></div>
              <div class="banner-content d-flex justify-content-between align-items-center flex-wrap gap-4 p-4">
                <div class="d-flex align-items-center gap-3">
                  <div class="user-avatar-wrap">
                    <div class="user-avatar-initials">
                      {{ getUserInitials(userInfo.names) }}
                    </div>
                    <div class="avatar-ring"></div>
                  </div>
                  <div>
                    <h5 class="user-name text-white mb-1">{{ userInfo.names }}</h5>
                    <p class="user-role mb-0"><i class="bx bx-user me-1"></i> Employee Portal</p>
                  </div>
                </div>
                
                <div class="expiry-widget p-3 rounded">
                  <div class="d-flex align-items-center gap-3">
                    <div class="widget-icon-box" :class="getExpiryWidgetClass(userInfo.passwordExpiry)">
                      <i class="bx bx-time-five font-size-22"></i>
                    </div>
                    <div>
                      <span class="widget-label d-block text-muted small">Password Expiry Status</span>
                      <span class="widget-value fw-bold text-white" :class="getExpiryTextClass(userInfo.passwordExpiry)">
                        {{ passwordExpiryDisplay }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="row">
              <!-- Left Column: Guidelines & Tips -->
              <div class="col-xl-4 col-lg-5 mb-4">
                <!-- Requirements Card -->
                <div class="modern-card card-glow mb-4">
                  <div class="card-header-modern">
                    <i class="bx bx-shield-quarter header-icon text-primary"></i>
                    <h5 class="card-title-modern">Password Requirements</h5>
                  </div>
                  <div class="card-body-modern">
                    <p class="text-muted small mb-3">Your new password must satisfy the following security standards:</p>
                    <div class="requirements-checklist">
                      <div class="checklist-item" :class="{ 'checklist-item--checked': passwordChecks.length }">
                        <div class="checkbox-indicator">
                          <i class="bx" :class="passwordChecks.length ? 'bx-check-circle' : 'bx-circle'"></i>
                        </div>
                        <span class="checklist-text">At least 8 characters long</span>
                      </div>
                      <div class="checklist-item" :class="{ 'checklist-item--checked': passwordChecks.uppercase }">
                        <div class="checkbox-indicator">
                          <i class="bx" :class="passwordChecks.uppercase ? 'bx-check-circle' : 'bx-circle'"></i>
                        </div>
                        <span class="checklist-text">At least one uppercase letter (A-Z)</span>
                      </div>
                      <div class="checklist-item" :class="{ 'checklist-item--checked': passwordChecks.lowercase }">
                        <div class="checkbox-indicator">
                          <i class="bx" :class="passwordChecks.lowercase ? 'bx-check-circle' : 'bx-circle'"></i>
                        </div>
                        <span class="checklist-text">At least one lowercase letter (a-z)</span>
                      </div>
                      <div class="checklist-item" :class="{ 'checklist-item--checked': passwordChecks.number }">
                        <div class="checkbox-indicator">
                          <i class="bx" :class="passwordChecks.number ? 'bx-check-circle' : 'bx-circle'"></i>
                        </div>
                        <span class="checklist-text">At least one number (0-9)</span>
                      </div>
                      <div class="checklist-item" :class="{ 'checklist-item--checked': passwordChecks.special }">
                        <div class="checkbox-indicator">
                          <i class="bx" :class="passwordChecks.special ? 'bx-check-circle' : 'bx-circle'"></i>
                        </div>
                        <span class="checklist-text">At least one special character (!@#$%^&*)</span>
                      </div>
                    </div>

                    <div class="expiry-alert-info mt-3 p-3 rounded">
                      <div class="d-flex gap-2">
                        <i class="bx bx-info-circle text-info font-size-18 mt-0-5"></i>
                        <span class="small text-muted">Passwords automatically expire every 45 days. You will receive a warning notice before expiration.</span>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Safety Practices Card -->
                <div class="modern-card card-glow mb-4">
                  <div class="card-header-modern">
                    <i class="bx bx-bulb header-icon text-warning"></i>
                    <h5 class="card-title-modern">Safety Practices</h5>
                  </div>
                  <div class="card-body-modern">
                    <ul class="safety-tips-list mb-0 ps-3">
                      <li>Avoid sharing your password with anyone.</li>
                      <li>Use a unique password not associated with other online accounts.</li>
                      <li>Avoid using predictable patterns like name, birth date, or common sequences.</li>
                      <li>Consider using a password manager to securely store credentials.</li>
                    </ul>
                  </div>
                </div>
              </div>

              <!-- Right Column: Update Password Form -->
              <div class="col-xl-8 col-lg-7 mb-4">
                <!-- Change Password Form Card -->
                <div class="modern-card mb-4">
                  <div class="card-header-modern border-bottom-0">
                    <i class="bx bx-lock-open-alt header-icon text-success"></i>
                    <h5 class="card-title-modern">Update Your Password</h5>
                  </div>
                  <div class="card-body-modern pt-0">
                    <form @submit.prevent="changePassword" class="modern-form">
                      <!-- Current Password -->
                      <div class="form-group-modern mb-4">
                        <label for="currentPassword" class="label-modern">
                          Current Password <span class="required-indicator">*</span>
                        </label>
                        <div class="input-wrapper-modern">
                          <span class="input-icon-left"><i class="bx bx-lock"></i></span>
                          <input
                              v-model="formData.currentPassword"
                              :type="showCurrentPassword ? 'text' : 'password'"
                              class="input-modern"
                              :class="{ 'input-modern--invalid': errors.currentPassword }"
                              id="currentPassword"
                              placeholder="Enter your current password"
                              required
                          />
                          <button
                              class="input-toggle-btn"
                              type="button"
                              @click="showCurrentPassword = !showCurrentPassword"
                              aria-label="Toggle password visibility"
                          >
                            <i class="bx" :class="showCurrentPassword ? 'bx-hide' : 'bx-show'"></i>
                          </button>
                        </div>
                        <div v-if="errors.currentPassword" class="error-msg-modern">
                          <i class="bx bx-error-circle me-1"></i> {{ errors.currentPassword }}
                        </div>
                      </div>

                      <!-- New Password -->
                      <div class="form-group-modern mb-4">
                        <label for="newPassword" class="label-modern">
                          New Password <span class="required-indicator">*</span>
                        </label>
                        <div class="input-wrapper-modern">
                          <span class="input-icon-left"><i class="bx bx-lock-alt"></i></span>
                          <input
                              v-model="formData.newPassword"
                              :type="showNewPassword ? 'text' : 'password'"
                              class="input-modern"
                              :class="{ 'input-modern--invalid': errors.newPassword }"
                              id="newPassword"
                              placeholder="Create a strong password"
                              required
                              @input="validatePassword"
                          />
                          <button
                              class="input-toggle-btn"
                              type="button"
                              @click="showNewPassword = !showNewPassword"
                              aria-label="Toggle password visibility"
                          >
                            <i class="bx" :class="showNewPassword ? 'bx-hide' : 'bx-show'"></i>
                          </button>
                        </div>
                        <div v-if="errors.newPassword" class="error-msg-modern">
                          <i class="bx bx-error-circle me-1"></i> {{ errors.newPassword }}
                        </div>

                        <!-- Animated Password Strength Meter -->
                        <transition name="fade">
                          <div v-if="formData.newPassword" class="strength-meter-wrap mt-3">
                            <div class="d-flex justify-content-between align-items-center mb-1">
                              <span class="strength-label">Password Strength:</span>
                              <span class="strength-text text-uppercase font-size-11" :class="getStrengthTextClass(passwordStrength)">
                                {{ passwordStrengthText }}
                              </span>
                            </div>
                            <div class="strength-bar-bg">
                              <div
                                  class="strength-bar-fill"
                                  :style="{ width: passwordStrengthPercentage + '%' }"
                                  :class="getStrengthBarClass(passwordStrength)"
                              ></div>
                            </div>
                          </div>
                        </transition>
                      </div>

                      <!-- Confirm New Password -->
                      <div class="form-group-modern mb-4">
                        <label for="confirmPassword" class="label-modern">
                          Confirm New Password <span class="required-indicator">*</span>
                        </label>
                        <div class="input-wrapper-modern">
                          <span class="input-icon-left"><i class="bx bx-shield-quarter"></i></span>
                          <input
                              v-model="formData.confirmPassword"
                              :type="showConfirmPassword ? 'text' : 'password'"
                              class="input-modern"
                              :class="{ 'input-modern--invalid': errors.confirmPassword }"
                              id="confirmPassword"
                              placeholder="Re-enter your new password"
                              required
                              @input="validateConfirmPassword"
                          />
                          <button
                              class="input-toggle-btn"
                              type="button"
                              @click="showConfirmPassword = !showConfirmPassword"
                              aria-label="Toggle password visibility"
                          >
                            <i class="bx" :class="showConfirmPassword ? 'bx-hide' : 'bx-show'"></i>
                          </button>
                        </div>
                        <div v-if="errors.confirmPassword" class="error-msg-modern">
                          <i class="bx bx-error-circle me-1"></i> {{ errors.confirmPassword }}
                        </div>
                        <div v-else-if="formData.confirmPassword && formData.newPassword === formData.confirmPassword" class="success-msg-modern">
                          <i class="bx bx-check-circle me-1"></i> Passwords match
                        </div>
                      </div>

                      <!-- Form Actions -->
                      <div class="form-actions-modern d-flex justify-content-end gap-3 mt-4 pt-3">
                        <button
                            type="button"
                            class="btn-modern btn-modern--secondary"
                            @click="resetForm"
                            :disabled="loading"
                        >
                          <i class="bx bx-refresh"></i> Reset Form
                        </button>
                        <button
                            type="submit"
                            class="btn-modern btn-modern--primary"
                            :disabled="loading || !isFormValid"
                        >
                          <span v-if="loading" class="d-flex align-items-center gap-2">
                            <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                            Updating...
                          </span>
                          <span v-else class="d-flex align-items-center gap-1">
                            <i class="bx bx-save"></i> Change Password
                          </span>
                        </button>
                      </div>
                    </form>
                  </div>
                </div>

                <!-- Additional Security Options -->
                <div class="row">
                  <div class="col-md-6 mb-4">
                    <div class="modern-card h-100 widget-card">
                      <div class="card-body-modern d-flex align-items-start gap-3 p-4">
                        <div class="widget-icon-box bg-light-primary text-primary">
                          <i class="bx bx-shield-check font-size-24"></i>
                        </div>
                        <div class="flex-grow-1">
                          <h6 class="widget-title mb-1">Two-Factor Auth (2FA)</h6>
                          <p class="text-muted small mb-3">Add a secondary verification step to secure your login attempts.</p>
                          <button class="btn-modern btn-modern--xs btn-modern--outline-primary" @click="manage2FA" :disabled="loading">
                            {{ userInfo.requireOTP === 'true' ? 'Disable 2FA' : 'Enable 2FA' }}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="col-md-6 mb-4">
                    <div class="modern-card h-100 widget-card">
                      <div class="card-body-modern d-flex align-items-start gap-3 p-4">
                        <div class="widget-icon-box bg-light-info text-info">
                          <i class="bx bx-history font-size-24"></i>
                        </div>
                        <div class="flex-grow-1">
                          <h6 class="widget-title mb-1">Session Logs</h6>
                          <p class="text-muted small mb-3">Monitor recent device activity and review active login details.</p>
                          <button class="btn-modern btn-modern--xs btn-modern--outline-info" @click="viewLoginHistory">
                            View History
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </HrPageLayout>
      </template>

<script>
/* eslint-disable */
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import { authUrl, execute, toast } from "@/api";

export default {
  name: "ChangePassword",
  components: {
    HrPageLayout
  },
  data() {
    return {
      loading: false,
      userInfo: {
        names: '',
        idNo: '',
        requireOTP: 'false',
        passwordExpiry: null
      },
      formData: {
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      },
      errors: {
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      },
      showCurrentPassword: false,
      showNewPassword: false,
      showConfirmPassword: false,
      passwordChecks: {
        length: false,
        uppercase: false,
        lowercase: false,
        number: false,
        special: false
      }
    };
  },
  computed: {
    isFormValid() {
      return (
          this.formData.currentPassword &&
          this.formData.newPassword &&
          this.formData.confirmPassword &&
          this.formData.newPassword === this.formData.confirmPassword &&
          this.passwordStrength >= 3 &&
          !this.errors.currentPassword &&
          !this.errors.newPassword &&
          !this.errors.confirmPassword
      );
    },
    passwordStrength() {
      let strength = 0;
      if (this.passwordChecks.length) strength++;
      if (this.passwordChecks.uppercase) strength++;
      if (this.passwordChecks.lowercase) strength++;
      if (this.passwordChecks.number) strength++;
      if (this.passwordChecks.special) strength++;
      return strength;
    },
    passwordStrengthPercentage() {
      return (this.passwordStrength / 5) * 100;
    },
    passwordStrengthText() {
      if (this.passwordStrength === 0) return 'Very Weak';
      if (this.passwordStrength === 1) return 'Weak';
      if (this.passwordStrength === 2) return 'Fair';
      if (this.passwordStrength === 3) return 'Good';
      if (this.passwordStrength === 4) return 'Strong';
      return 'Very Strong';
    },
    passwordStrengthClass() {
      if (this.passwordStrength <= 1) return 'text-danger bg-danger';
      if (this.passwordStrength === 2) return 'text-warning bg-warning';
      if (this.passwordStrength === 3) return 'text-info bg-info';
      return 'text-success bg-success';
    },
    passwordExpiryDisplay() {
      if (!this.userInfo.passwordExpiry) return 'Not set';
      const expiryDate = new Date(this.userInfo.passwordExpiry);
      const today = new Date();
      const daysRemaining = Math.ceil((expiryDate - today) / (1000 * 60 * 60 * 24));

      if (daysRemaining < 0) {
        return 'Expired';
      } else if (daysRemaining === 0) {
        return 'Expires today';
      } else if (daysRemaining <= 7) {
        return `${daysRemaining} days remaining`;
      } else {
        return expiryDate.toLocaleDateString();
      }
    }
  },
  mounted() {
    this.loadUserInfo();
  },
  methods: {
    loadUserInfo() {
      try {
        const userInfoStr = sessionStorage.getItem('userInfo');
        if (userInfoStr) {
          this.userInfo = JSON.parse(userInfoStr);
        }
      } catch (error) {
        console.error('Error loading user info:', error);
        toast.error('Failed to load user information');
      }
    },

    goToDashboard() {
      this.$router.push('/dashboard');
    },

    getUserInitials(name) {
      if (!name) return 'U';
      const parts = name.trim().split(/\s+/);
      if (parts.length >= 2) {
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
      }
      return parts[0][0].toUpperCase();
    },

    getExpiryWidgetClass(expiry) {
      if (!expiry) return 'widget-expiry--safe';
      const expiryDate = new Date(expiry);
      const today = new Date();
      const daysRemaining = Math.ceil((expiryDate - today) / (1000 * 60 * 60 * 24));
      if (daysRemaining < 0) return 'widget-expiry--expired';
      if (daysRemaining <= 7) return 'widget-expiry--critical';
      return 'widget-expiry--safe';
    },

    getExpiryTextClass(expiry) {
      if (!expiry) return 'text-success';
      const expiryDate = new Date(expiry);
      const today = new Date();
      const daysRemaining = Math.ceil((expiryDate - today) / (1000 * 60 * 60 * 24));
      if (daysRemaining < 0) return 'text-danger';
      if (daysRemaining <= 7) return 'text-warning';
      return 'text-success';
    },

    getStrengthTextClass(strength) {
      if (strength <= 1) return 'strength-text--weak';
      if (strength === 2) return 'strength-text--fair';
      if (strength === 3) return 'strength-text--good';
      return 'strength-text--strong';
    },

    getStrengthBarClass(strength) {
      if (strength <= 1) return 'strength-bar--weak';
      if (strength === 2) return 'strength-bar--fair';
      if (strength === 3) return 'strength-bar--good';
      return 'strength-bar--strong';
    },

    validatePassword() {
      const password = this.formData.newPassword;

      // Update password checks
      this.passwordChecks.length = password.length >= 8;
      this.passwordChecks.uppercase = /[A-Z]/.test(password);
      this.passwordChecks.lowercase = /[a-z]/.test(password);
      this.passwordChecks.number = /[0-9]/.test(password);
      this.passwordChecks.special = /[!@#$%^&*(),.?":{}|<>]/.test(password);

      // Clear error if password meets all requirements
      if (this.passwordStrength >= 3) {
        this.errors.newPassword = '';
      } else if (password) {
        this.errors.newPassword = 'Password must meet the minimum requirements';
      }

      // Validate confirm password if it's been entered
      if (this.formData.confirmPassword) {
        this.validateConfirmPassword();
      }
    },

    validateConfirmPassword() {
      if (this.formData.newPassword !== this.formData.confirmPassword) {
        this.errors.confirmPassword = 'Passwords do not match';
      } else {
        this.errors.confirmPassword = '';
      }
    },

    resetForm() {
      this.formData = {
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      };
      this.errors = {
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      };
      this.showCurrentPassword = false;
      this.showNewPassword = false;
      this.showConfirmPassword = false;
      this.passwordChecks = {
        length: false,
        uppercase: false,
        lowercase: false,
        number: false,
        special: false
      };
    },

    async changePassword() {
      // Final validation
      if (!this.isFormValid) {
        toast.error('Please fill in all required fields correctly');
        return;
      }

      // Check if new password is same as current
      if (this.formData.currentPassword === this.formData.newPassword) {
        this.errors.newPassword = 'New password must be different from current password';
        toast.error('New password must be different from current password');
        return;
      }

      this.loading = true;

      const data = new FormData();
      data.append("function", "changePassword");
      data.append("currentPassword", btoa(this.formData.currentPassword));
      data.append("newPassword", btoa(this.formData.newPassword));
      data.append("idNo", this.userInfo.idNo);

      try {
        const res = await execute(data, authUrl);

        this.loading = false;

        if (res.data.success) {
          toast.success(res.data.message || 'Password changed successfully');

          // Reset form
          this.resetForm();

          toast.info('Please login with your new password');
          // Clear session and redirect to login immediately
          sessionStorage.clear();
          this.$router.push('/');
        } else {
          toast.error(res.data.message || 'Failed to change password');

          // Handle specific error for wrong current password
          if (res.data.message && res.data.message.includes('current password')) {
            this.errors.currentPassword = res.data.message;
          }
        }
      } catch (error) {
        this.loading = false;
        console.error('Error changing password:', error);
        const backendMessage = error?.response?.data?.message || error?.response?.data?.error;
        toast.error(backendMessage || error.message || 'An error occurred while changing password');
      }
    },

    async manage2FA() {
      const currentOtp = this.userInfo.requireOTP === 'true';
      const confirmMsg = currentOtp
        ? "Disable Two-Factor Authentication? You will no longer receive OTP codes during login."
        : "Enable Two-Factor Authentication? You will receive a secure OTP code on your registered email/phone during login.";

      if (!confirm(confirmMsg)) return;

      this.loading = true;
      const data = new FormData();
      data.append("function", "updateUserOTP");
      data.append("requireOTP", currentOtp ? "false" : "true");

      try {
        const res = await execute(data, authUrl);
        this.loading = false;
        if (res.data.success) {
          const newVal = currentOtp ? "false" : "true";
          this.userInfo.requireOTP = newVal;

          const userInfoStr = sessionStorage.getItem('userInfo');
          if (userInfoStr) {
            const parsed = JSON.parse(userInfoStr);
            parsed.requireOTP = newVal;
            sessionStorage.setItem('userInfo', JSON.stringify(parsed));
          }

          toast.success(res.data.message || 'Two-Factor Authentication updated successfully');
        } else {
          toast.error(res.data.message || 'Failed to update Two-Factor Authentication');
        }
      } catch (error) {
        this.loading = false;
        console.error('Error updating 2FA:', error);
        const backendMessage = error?.response?.data?.message || error?.response?.data?.error;
        toast.error(backendMessage || error.message || 'An error occurred while updating Two-Factor Authentication');
      }
    },

    viewLoginHistory() {
      toast.info('Login history coming soon');
      // TODO: Implement login history view
    }
  }
};
</script>

<style scoped>
.change-password-page {
  --primary-color: #2195d3;
  --primary-hover: #176f9d;
  --primary-glow: rgba(33, 149, 211, 0.15);
  --success-color: #10b981;
  --success-glow: rgba(16, 185, 129, 0.15);
  --warning-color: #f59e0b;
  --danger-color: #ef4444;
  --danger-glow: rgba(239, 68, 68, 0.15);
  --card-bg: #ffffff;
  --input-bg: #f8fafc;
  --border-color: #e2e8f0;
  --text-primary: #1e293b;
  --text-secondary: #64748b;
  
  font-family: 'Inter', system-ui, -apple-system, sans-serif;
  color: var(--text-primary);
}

/* Page Layout Shell */
#layout-wrapper {
  background-color: #f8fafc;
  min-height: 100vh;
}

/* Modern Page Header */
.modern-header {
  padding-bottom: 12px;
  border-bottom: 1px solid var(--border-color);
}
.page-title {
  font-size: 22px;
  font-weight: 700;
  color: #111827;
  letter-spacing: -0.025em;
}
.modern-breadcrumb {
  background: transparent;
  padding: 0;
}
.modern-breadcrumb .breadcrumb-item a {
  color: var(--text-secondary);
  text-decoration: none;
  font-weight: 500;
  transition: color 0.2s;
}
.modern-breadcrumb .breadcrumb-item a:hover {
  color: var(--primary-color);
}
.modern-breadcrumb .breadcrumb-item.active {
  color: var(--text-primary);
  font-weight: 600;
}

/* Profile Cover Banner */
.profile-security-banner {
  position: relative;
  border-radius: 16px;
  background: linear-gradient(135deg, #16365c 0%, #2195d3 100%);
  color: white;
  overflow: hidden;
  box-shadow: 0 10px 25px rgba(33, 149, 211, 0.15);
}
.banner-overlay {
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  background: radial-gradient(circle at top right, rgba(255, 255, 255, 0.12), transparent 60%);
  pointer-events: none;
}
.banner-content {
  position: relative;
  z-index: 2;
}
.user-avatar-wrap {
  position: relative;
  width: 64px;
  height: 64px;
}
.user-avatar-initials {
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(8px);
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  font-weight: 700;
  color: white;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}
.avatar-ring {
  position: absolute;
  top: -4px; left: -4px; right: -4px; bottom: -4px;
  border: 2px dashed rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  animation: rotate-ring 20s linear infinite;
}
@keyframes rotate-ring {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
.user-name {
  font-size: 18px;
  font-weight: 700;
  letter-spacing: -0.01em;
}
.user-role {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.8);
}

/* Expiry Widget */
.expiry-widget {
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.15);
  min-width: 240px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
}
.widget-icon-box {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s;
}
.widget-expiry--safe {
  background-color: rgba(16, 185, 129, 0.2);
  color: #34d399 !important;
}
.widget-expiry--critical {
  background-color: rgba(245, 158, 11, 0.2);
  color: #fbbf24 !important;
}
.widget-expiry--expired {
  background-color: rgba(239, 68, 68, 0.2);
  color: #f87171 !important;
}
.widget-label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: rgba(255, 255, 255, 0.6) !important;
}
.widget-value {
  font-size: 15px;
}

/* Modern Cards */
.modern-card {
  background: var(--card-bg);
  border: 1px solid var(--border-color);
  border-radius: 16px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.01);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
}
.modern-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 12px 30px rgba(33, 149, 211, 0.06);
  border-color: rgba(33, 149, 211, 0.25);
}
.card-glow {
  position: relative;
}
.card-glow::after {
  content: '';
  position: absolute;
  top: 0; left: 0; right: 0; height: 4px;
  background: linear-gradient(90deg, var(--primary-color), #6366f1);
  opacity: 0.8;
}
.card-header-modern {
  padding: 24px 24px 16px;
  display: flex;
  align-items: center;
  gap: 12px;
}
.card-title-modern {
  font-size: 16px;
  font-weight: 700;
  margin: 0;
  color: #111827;
}
.header-icon {
  font-size: 20px;
}
.card-body-modern {
  padding: 0 24px 24px;
}

/* Requirements Checklist */
.requirements-checklist {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.checklist-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  background: #f8fafc;
  border-radius: 8px;
  border: 1px solid #f1f5f9;
  transition: all 0.2s ease;
}
.checklist-item--checked {
  background: #f0fdf4;
  border-color: rgba(16, 185, 129, 0.2);
}
.checkbox-indicator {
  font-size: 16px;
  color: #94a3b8;
  display: flex;
  align-items: center;
  transition: all 0.2s;
}
.checklist-item--checked .checkbox-indicator {
  color: var(--success-color);
  animation: check-pop 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}
@keyframes check-pop {
  0% { transform: scale(0.6); }
  100% { transform: scale(1.1); }
}
.checklist-text {
  font-size: 13px;
  color: var(--text-secondary);
  transition: color 0.2s;
}
.checklist-item--checked .checklist-text {
  color: #166534;
  font-weight: 500;
}

/* Expiry alert box */
.expiry-alert-info {
  background: rgba(33, 149, 211, 0.06);
  border: 1px dashed rgba(33, 149, 211, 0.2);
}
.mt-0-5 {
  margin-top: 2px;
}

/* Safety list */
.safety-tips-list {
  padding-left: 20px;
}
.safety-tips-list li {
  font-size: 13px;
  color: var(--text-secondary);
  margin-bottom: 8px;
  line-height: 1.4;
}

/* Form inputs styling */
.label-modern {
  display: block;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #475569;
  margin-bottom: 6px;
}
.required-indicator {
  color: var(--danger-color);
}
.input-wrapper-modern {
  position: relative;
  display: flex;
  align-items: center;
}
.input-icon-left {
  position: absolute;
  left: 14px;
  color: #94a3b8;
  font-size: 18px;
  transition: color 0.2s;
  display: flex;
  align-items: center;
}
.input-modern {
  width: 100%;
  padding: 12px 48px 12px 42px;
  background-color: var(--input-bg);
  border: 1.5px solid var(--border-color);
  border-radius: 10px;
  color: var(--text-primary);
  font-size: 14px;
  transition: all 0.2s ease;
  outline: none;
}
.input-modern:focus {
  border-color: var(--primary-color);
  background-color: white;
  box-shadow: 0 0 0 4px var(--primary-glow);
}
.input-modern:focus ~ .input-icon-left {
  color: var(--primary-color);
}
.input-modern--invalid {
  border-color: var(--danger-color);
  background-color: white;
}
.input-modern--invalid:focus {
  box-shadow: 0 0 0 4px var(--danger-glow);
}
.input-toggle-btn {
  position: absolute;
  right: 14px;
  border: none;
  background: transparent;
  color: #94a3b8;
  font-size: 18px;
  cursor: pointer;
  padding: 4px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  outline: none;
  z-index: 10;
}
.input-toggle-btn:hover {
  color: var(--text-primary);
  background-color: rgba(0, 0, 0, 0.05);
}

/* Error/Success messages */
.error-msg-modern {
  color: var(--danger-color);
  font-size: 12px;
  font-weight: 500;
  margin-top: 6px;
  display: flex;
  align-items: center;
}
.success-msg-modern {
  color: var(--success-color);
  font-size: 12px;
  font-weight: 500;
  margin-top: 6px;
  display: flex;
  align-items: center;
}

/* Strength Meter */
.strength-meter-wrap {
  padding: 12px;
  background: #f8fafc;
  border-radius: 8px;
  border: 1px solid #f1f5f9;
}
.strength-label {
  font-size: 11px;
  color: var(--text-secondary);
}
.strength-text {
  font-size: 11px;
  font-weight: 700;
}
.strength-text--weak { color: var(--danger-color); }
.strength-text--fair { color: var(--warning-color); }
.strength-text--good { color: #3b82f6; }
.strength-text--strong { color: var(--success-color); }

.strength-bar-bg {
  height: 6px;
  background-color: #e2e8f0;
  border-radius: 3px;
  overflow: hidden;
}
.strength-bar-fill {
  height: 100%;
  border-radius: 3px;
  transition: width 0.4s cubic-bezier(0.4, 0, 0.2, 1), background-color 0.3s;
}
.strength-bar--weak { background: linear-gradient(90deg, #ef4444, #f87171); }
.strength-bar--fair { background: linear-gradient(90deg, #f59e0b, #fbbf24); }
.strength-bar--good { background: linear-gradient(90deg, #3b82f6, #60a5fa); }
.strength-bar--strong { background: linear-gradient(90deg, #10b981, #34d399); }

/* Buttons modern styling */
.btn-modern {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  padding: 11px 22px;
  border-radius: 10px;
  border: none;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  outline: none;
}
.btn-modern--primary {
  background: linear-gradient(135deg, var(--primary-color) 0%, #1e4976 100%);
  color: white;
  box-shadow: 0 4px 12px rgba(33, 149, 211, 0.25);
}
.btn-modern--primary:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 18px rgba(33, 149, 211, 0.35);
  filter: brightness(1.05);
}
.btn-modern--primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}
.btn-modern--secondary {
  background-color: #f1f5f9;
  color: #475569;
  border: 1.5px solid #e2e8f0;
}
.btn-modern--secondary:hover:not(:disabled) {
  background-color: #e2e8f0;
  color: #1e293b;
}

/* Widgets Security options */
.widget-card {
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.01);
}
.bg-light-primary {
  background-color: rgba(33, 149, 211, 0.08);
}
.bg-light-info {
  background-color: rgba(6, 182, 212, 0.08);
}
.bg-light-primary i {
  color: var(--primary-color);
}
.bg-light-info i {
  color: #0891b2;
}
.widget-title {
  font-size: 14px;
  font-weight: 700;
  color: #111827;
}
.btn-modern--xs {
  font-size: 12px;
  padding: 6px 14px;
  border-radius: 8px;
}
.btn-modern--outline-primary {
  background: transparent;
  color: var(--primary-color);
  border: 1.5px solid var(--primary-color);
}
.btn-modern--outline-primary:hover {
  background: var(--primary-light);
}
.btn-modern--outline-info {
  background: transparent;
  color: #0891b2;
  border: 1.5px solid #0891b2;
}
.btn-modern--outline-info:hover {
  background: rgba(6, 182, 212, 0.08);
}

/* Fade animation */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter-from, .fade-leave-to {
  opacity: 0;
}

/* Responsive adjustment */
@media (max-width: 991.98px) {
  .profile-security-banner {
    flex-direction: column;
    align-items: flex-start !important;
  }
  .expiry-widget {
    width: 100%;
  }
}
@media (max-width: 575.98px) {
  .card-body-modern {
    padding: 0 16px 20px;
  }
  .card-header-modern {
    padding: 20px 16px 12px;
  }
  .btn-modern {
    width: 100%;
  }
  .form-actions-modern {
    flex-direction: column-reverse;
  }
}
</style>